import { FunctionComponent, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { NavItem } from 'components/index';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { IPostPaymentsCount, POST_PAYMENT_COUNT } from 'schemas';
import { SettingsContext } from 'settings';
import { EPaymentMethod, EPaymentStatus } from 'types';

import { IMenuItem } from '../interfaces';
import { getMenuItems } from '../utils';
import s from './Menu.module.scss';
import { NavGroup } from './NavGroup';
import { IReviewsCount, REVIEWS_COUNT } from './schema';

export const Menu: FunctionComponent = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { feedbackEnabled, postPaymentEnabled, countryCode },
    },
  } = useContext(SettingsContext);

  const isReviewsAvailable = useIsFeatureAvailable('club_review');
  const isPostPaymentAvailable = useIsFeatureAvailable('post_payments');
  const isBookingAvailable = useIsFeatureAvailable('booking');
  const isNewsAvailable = useIsFeatureAvailable('club_news');
  const isComboAvailable = useIsFeatureAvailable('combos');

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data } = useQuery<IReviewsCount>(REVIEWS_COUNT, {
    fetchPolicy: 'cache-and-network',
    skip: !isReviewsAvailable || !permissions?.has('club_comments_browse'),
    pollInterval: feedbackEnabled ? 60000 : undefined,
  });

  const { data: PaymentData } = useQuery<IPostPaymentsCount>(
    POST_PAYMENT_COUNT,
    {
      fetchPolicy: 'cache-and-network',
      skip:
        !isPostPaymentAvailable ||
        !permissions?.has('work_shift_active') ||
        !postPaymentEnabled,
      pollInterval: feedbackEnabled ? 60000 : undefined,
    }
  );

  const reviewsCount = data?.clubComments.summary.unread_total;

  const postPaymentCount = useMemo(
    () =>
      PaymentData?.activeWorkShift?.payments?.filter(
        ({ method, status }) =>
          method === EPaymentMethod.POSTPAID &&
          status === EPaymentStatus.PENDING
      ).length || 0,
    [PaymentData]
  );

  const MENU_ITEMS = useMemo<readonly IMenuItem[] | null>(() => {
    if (!permissions) return null;

    return getMenuItems({
      permissions,
      reviewsCount,
      isBookingAvailable,
      isComboAvailable,
      postPaymentCount,
      isNewsAvailable,
      countryCode,
    });
  }, [
    permissions,
    reviewsCount,
    isBookingAvailable,
    isComboAvailable,
    postPaymentCount,
    isNewsAvailable,
    countryCode,
  ]);

  return (
    <div className={s.container}>
      <ul className={cn(s['menu-container'])}>
        <Preloader loading={!permissions && !MENU_ITEMS}>
          {MENU_ITEMS?.map((item) => {
            return (
              <li
                key={item.title}
                {...(item.showSeparator
                  ? { className: s['show-separator'] }
                  : {})}
              >
                {item.subItems ? (
                  <NavGroup
                    items={item.subItems}
                    title={t(item.title)}
                    Icon={item.Icon}
                  />
                ) : (
                  <NavItem
                    to={item.to}
                    Icon={item.Icon}
                    title={t(item.title)}
                    disabled={item?.disabled}
                    count={item.count}
                    featureDisabled={item.featureDisabled}
                    isNew={item?.isNew}
                  />
                )}
              </li>
            );
          })}
        </Preloader>
      </ul>
    </div>
  );
};
